// Locale-aware formatting of hiking quantities, ported from TrailBivvy/DesignSystem/Formatters.swift (TrailBivvyIOS):
// metric or US customary units (a user preference, "automatic" following the region like iOS
// `Locale.measurementSystem`), numbers and dates in the user's locale through Intl.
//
// Every function reads the active `formatLocale` and `unitSystem` signals (so a component re-renders when either
// changes) unless `options` pass explicit values (tests, previews). Inputs keep the Swift units: metres, seconds,
// °C, km/h, m/s, mm, fractions 0…1; dates are epoch milliseconds.

import { computed, signal } from '@preact/signals';
import type { EpochMs } from '../core/models';
import { formatLocale } from './index';

/** User preference for units. `automatic` follows the region (US, Liberia, Myanmar → imperial). */
export type UnitSystemPreference = 'automatic' | 'metric' | 'imperial';
export type UnitSystem = 'metric' | 'imperial';

export const unitSystemPreferences: readonly UnitSystemPreference[] = ['automatic', 'metric', 'imperial'];

/** The persisted preference (set by the settings store; `Format.unitSystem` source in iOS AppEnvironment). */
export const unitPreference = signal<UnitSystemPreference>('automatic');

/** Regions whose CLDR measurement system is "US" (Foundation `Locale.MeasurementSystem.us`). */
const usMeasurementRegions = new Set(['US', 'LR', 'MM']);

function regionOf(tag: string): string | undefined {
  try {
    return new Intl.Locale(tag).region;
  } catch {
    return undefined;
  }
}

/**
 * `Format.resolve`: explicit choices win; automatic → imperial only for a US-measurement region. A tag without a
 * region (a browser that reports only "en") resolves to metric: iOS always knows the device region, a browser may
 * not, and guessing "en" → US would switch most of the world's English readers to feet.
 */
export function resolveUnitSystem(preference: UnitSystemPreference, localeTag: string): UnitSystem {
  switch (preference) {
    case 'metric':
      return 'metric';
    case 'imperial':
      return 'imperial';
    case 'automatic':
      return usMeasurementRegions.has(regionOf(localeTag) ?? '') ? 'imperial' : 'metric';
  }
}

/** Active unit system (`Format.unitSystem`). */
export const unitSystem = computed<UnitSystem>(() => resolveUnitSystem(unitPreference.value, formatLocale.value));

export interface FormatOptions {
  /** BCP 47 locale; default `formatLocale`. */
  locale?: string;
  /** Unit system; default `unitSystem`. */
  units?: UnitSystem;
  /** IANA time zone for dates; default the device's. */
  timeZone?: string;
}

/**
 * Joins a leading sign or arrow (↑, ↓, ±) to its value: a no-break space (U+00A0), so a line never ends with the
 * sign alone.
 */
export const signSpace = ' ';

const DASH = '–';

// MARK: - Intl caches (formatters are expensive to create; map labels format hundreds of values)

const cache = new Map<string, unknown>();

function cached<T>(id: string, make: () => T): T {
  let value = cache.get(id) as T | undefined;
  if (value === undefined) {
    value = make();
    cache.set(id, value);
  }
  return value;
}

function localeOf(options?: FormatOptions): string {
  return options?.locale ?? formatLocale.value;
}

function isImperial(options?: FormatOptions): boolean {
  return (options?.units ?? unitSystem.value) === 'imperial';
}

/**
 * Swift `Measurement.formatted(.measurement(width: .abbreviated, usage: .asProvided,
 * numberFormatStyle: .number.precision(.fractionLength(min...max))))`. ICU rounds half to even, as Foundation does.
 */
function measure(value: number, unit: string, minDigits: number, maxDigits: number, options?: FormatOptions): string {
  const tag = localeOf(options);
  const format = cached(
    `u|${tag}|${unit}|${minDigits}|${maxDigits}`,
    () =>
      new Intl.NumberFormat(tag, {
        style: 'unit',
        unit,
        unitDisplay: 'short',
        minimumFractionDigits: minDigits,
        maximumFractionDigits: maxDigits,
        roundingMode: 'halfEven',
      }),
  );
  return format.format(value);
}

/** Swift `Double.rounded()`: to nearest, halves away from zero (Math.round rounds -2.5 to -2). */
function rounded(value: number): number {
  return Math.sign(value) * Math.round(Math.abs(value));
}

// MARK: - Lengths

/** Metric: "850 m" below 1 km, "4.8 km" above (whole km from 100 km). Imperial: "500 ft" below 0.1 mi, "12.4 mi". */
export function distance(meters: number, options?: FormatOptions): string {
  if (!Number.isFinite(meters)) return DASH;
  const m = Math.abs(meters);
  if (isImperial(options)) {
    const miles = m / 1609.344;
    if (miles < 0.1) return measure(rounded(m / 0.3048 / 10) * 10, 'foot', 0, 0, options);
    return measure(miles, 'mile', 0, miles < 100 ? 1 : 0, options);
  }
  if (m < 1000) return measure(rounded(m / 10) * 10, 'meter', 0, 0, options);
  const km = m / 1000;
  return measure(km, 'kilometer', 0, km < 100 ? 1 : 0, options);
}

/** GPS accuracy: "± 6 m" / "± 20 ft" (no-break space after the sign). */
export function accuracy(meters: number, options?: FormatOptions): string {
  if (!Number.isFinite(meters) || meters < 0) return DASH;
  if (isImperial(options)) {
    const feet = meters / 0.3048;
    return '±' + signSpace + measure(feet < 300 ? rounded(feet) : rounded(feet / 10) * 10, 'foot', 0, 0, options);
  }
  const value = meters < 100 ? rounded(meters) : rounded(meters / 10) * 10;
  return '±' + signSpace + measure(value, 'meter', 0, 0, options);
}

/** "2,778 m" / "9,114 ft". */
export function elevation(meters: number, options?: FormatOptions): string {
  if (!Number.isFinite(meters)) return DASH;
  if (isImperial(options)) return measure(rounded(meters / 0.3048), 'foot', 0, 0, options);
  return measure(rounded(meters), 'meter', 0, 0, options);
}

/** Compact axis label in kilometres whatever the unit system: "5 km", "12.5 km" (`RouteText.axisKilometres`). */
export function axisKilometres(km: number, options?: FormatOptions): string {
  if (!Number.isFinite(km)) return DASH;
  return measure(km, 'kilometer', 0, 1, options);
}

/** "↑ 1,240 m" (no-break space after the arrow). */
export function ascent(meters: number, options?: FormatOptions): string {
  return '↑' + signSpace + elevation(meters, options);
}

/** "↓ 980 m" (no-break space after the arrow). */
export function descent(meters: number, options?: FormatOptions): string {
  return '↓' + signSpace + elevation(meters, options);
}

// MARK: - Time

/**
 * "3 hr, 20 min" (localized abbreviated units), at 5-minute resolution — more precision is false precision. Swift
 * `Duration.formatted(.units(allowed: [.hours, .minutes], width: .abbreviated))`: zero units are hidden, "0 min" for
 * nothing. Built from Intl unit + list formats (identical output where Intl.DurationFormat is missing).
 */
export function duration(seconds: number, options?: FormatOptions): string {
  if (!Number.isFinite(seconds)) return DASH;
  const total = Math.max(0, rounded(seconds / 300) * 300);
  const hours = Math.floor(total / 3600);
  const minutes = Math.round((total - hours * 3600) / 60);
  const tag = localeOf(options);
  const parts: string[] = [];
  if (hours > 0) parts.push(measure(hours, 'hour', 0, 0, options));
  if (minutes > 0 || hours === 0) parts.push(measure(minutes, 'minute', 0, 0, options));
  const list = cached(`l|${tag}`, () => new Intl.ListFormat(tag, { type: 'unit', style: 'short' }));
  return list.format(parts);
}

function dateFormat(
  id: string,
  options: FormatOptions | undefined,
  style: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const tag = localeOf(options);
  const timeZone = options?.timeZone;
  return cached(
    `d|${id}|${tag}|${timeZone ?? ''}`,
    () => new Intl.DateTimeFormat(tag, timeZone ? { ...style, timeZone } : style),
  );
}

/** Clock time, e.g. "17:45" / "5:45 PM". */
export function time(date: EpochMs, options?: FormatOptions): string {
  return dateFormat('time', options, { timeStyle: 'short' }).format(date);
}

/**
 * Hour width of the locale's short time ("06:30" in en-GB and de, "6:30" in en-US and es): Intl would otherwise
 * narrow the hour of a combined date-time pattern to the requested width, unlike Foundation's `.hour()`.
 */
function hourWidth(tag: string): '2-digit' | 'numeric' {
  return cached(`h|${tag}`, () => {
    const format = new Intl.DateTimeFormat(tag, { timeStyle: 'short', timeZone: 'UTC' });
    const parts = format.formatToParts(Date.UTC(2020, 0, 1, 6, 5));
    return parts.find((p) => p.type === 'hour')?.value.length === 2 ? '2-digit' : 'numeric';
  });
}

/** Day + time, e.g. "Sat 14 Aug, 17:45". */
export function dayTime(date: EpochMs, options?: FormatOptions): string {
  return dateFormat('dayTime', options, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: hourWidth(localeOf(options)),
    minute: '2-digit',
  }).format(date);
}

/** "Saturday 14 August". */
export function day(date: EpochMs, options?: FormatOptions): string {
  return dateFormat('day', options, { weekday: 'long', day: 'numeric', month: 'long' }).format(date);
}

/** Abbreviated date, e.g. "14 Aug 2026" / "Aug 14, 2026". */
export function date(value: EpochMs, options?: FormatOptions): string {
  return dateFormat('date', options, { dateStyle: 'medium' }).format(value);
}

/**
 * A day-precision data date (check, retrieval or review date stored as midnight UTC), formatted in UTC so it shows the
 * same day in every time zone.
 */
export function dataDay(value: EpochMs, options?: FormatOptions): string {
  return date(value, { ...options, timeZone: 'UTC' });
}

/**
 * A recurring day of the year without a year, e.g. "20 Dec" / "Dec 20" (`.dateTime.day().month(.abbreviated)` in
 * RoutePresentation.swift); 29 Feb stays representable. Empty for an impossible month or day.
 */
export function monthDay(month: number, dayOfMonth: number, options?: FormatOptions): string {
  const validMonth = Number.isInteger(month) && month >= 1 && month <= 12;
  if (!validMonth || !Number.isInteger(dayOfMonth) || dayOfMonth < 1 || dayOfMonth > 31) return '';
  // 2024 is a leap year; noon UTC formatted in UTC cannot shift to another day.
  const value = Date.UTC(2024, month - 1, dayOfMonth, 12);
  return dateFormat('monthDay', { ...options, timeZone: 'UTC' }, { day: 'numeric', month: 'short' }).format(value);
}

/** Standalone month name, e.g. "June" (`Calendar.standaloneMonthSymbols`). Empty for a month outside 1…12. */
export function monthName(month: number, options?: FormatOptions): string {
  if (!Number.isInteger(month) || month < 1 || month > 12) return '';
  const value = Date.UTC(2024, month - 1, 15, 12);
  return dateFormat('month', { ...options, timeZone: 'UTC' }, { month: 'long' }).format(value);
}

type RelativeUnit ='year' | 'month' | 'week' | 'day' | 'hour' | 'minute' | 'second';

function addMonths(epoch: EpochMs, months: number): EpochMs {
  const d = new Date(epoch);
  const dayOfMonth = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(dayOfMonth, lastDay));
  return d.getTime();
}

/**
 * Calendar difference from `from` to `to` in the largest non-zero unit, truncated, like Foundation's
 * `dateComponents([.year, .month, .weekOfMonth, .day, .hour, .minute, .second], from:to:)` that
 * `RelativeDateTimeFormatter` uses. Negative when `to` is before `from`.
 */
export function relativeComponents(to: EpochMs, from: EpochMs): { value: number; unit: RelativeUnit } {
  const sign = to >= from ? 1 : -1;
  const start = Math.min(from, to);
  const end = Math.max(from, to);
  const startDate = new Date(start);
  const endDate = new Date(end);
  let months = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());
  while (months > 0 && addMonths(start, months) > end) months -= 1;
  if (months >= 12) return { value: sign * Math.floor(months / 12), unit: 'year' };
  if (months > 0) return { value: sign * months, unit: 'month' };
  const seconds = Math.floor((end - start) / 1000);
  const steps: [RelativeUnit, number][] = [
    ['week', 604_800],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];
  for (const [unit, size] of steps) {
    if (seconds >= size) return { value: sign * Math.floor(seconds / size), unit };
  }
  return { value: sign * seconds, unit: 'second' };
}

/** "2 hours ago" / "in 3 days" relative to `now` (`RelativeDateTimeFormatter`, unitsStyle .full, numeric). */
export function relative(value: EpochMs, now: EpochMs, options?: FormatOptions): string {
  const tag = localeOf(options);
  const { value: amount, unit } = relativeComponents(value, now);
  const format = cached(`r|${tag}`, () => new Intl.RelativeTimeFormat(tag, { numeric: 'always', style: 'long' }));
  return format.format(amount, unit);
}

// MARK: - Weather

/** "12 °C" / "54 °F". */
export function temperature(celsius: number, options?: FormatOptions): string {
  if (!Number.isFinite(celsius)) return DASH;
  if (isImperial(options)) return measure((celsius * 9) / 5 + 32, 'fahrenheit', 0, 0, options);
  return measure(celsius, 'celsius', 0, 0, options);
}

/** "35 km/h" / "22 mph". */
export function windSpeed(kmh: number, options?: FormatOptions): string {
  if (!Number.isFinite(kmh)) return DASH;
  if (isImperial(options)) return measure(kmh / 1.609344, 'mile-per-hour', 0, 0, options);
  return measure(kmh, 'kilometer-per-hour', 0, 0, options);
}

/** "2.4 mm" / "0.09 in". */
export function precipitation(mm: number, options?: FormatOptions): string {
  if (!Number.isFinite(mm)) return DASH;
  if (isImperial(options)) return measure(mm / 25.4, 'inch', 0, 2, options);
  return measure(mm, 'millimeter', 0, mm < 10 ? 1 : 0, options);
}

/** "40 %" from a fraction 0…1. */
export function percent(fraction: number, options?: FormatOptions): string {
  if (!Number.isFinite(fraction)) return DASH;
  const tag = localeOf(options);
  return cached(
    `p|${tag}`,
    () => new Intl.NumberFormat(tag, { style: 'percent', maximumFractionDigits: 0, roundingMode: 'halfEven' }),
  ).format(fraction);
}

/** "4.2 km/h" / "2.6 mph" from metres per second. */
export function walkingSpeed(metersPerSecond: number, options?: FormatOptions): string {
  if (!Number.isFinite(metersPerSecond)) return DASH;
  if (isImperial(options)) return measure(metersPerSecond * 2.236936, 'mile-per-hour', 1, 1, options);
  return measure(metersPerSecond * 3.6, 'kilometer-per-hour', 1, 1, options);
}

/** "4°". */
export function degrees(value: number, options?: FormatOptions): string {
  if (!Number.isFinite(value)) return DASH;
  return number(value, 0, 0, options) + '°';
}

// MARK: - Numbers and bytes

/** A plain localized number with the given fraction digits (Swift `.number.precision(.fractionLength(min...max))`). */
export function number(value: number, minFraction = 0, maxFraction = 0, options?: FormatOptions): string {
  if (!Number.isFinite(value)) return DASH;
  const tag = localeOf(options);
  return cached(
    `n|${tag}|${minFraction}|${maxFraction}`,
    () =>
      new Intl.NumberFormat(tag, {
        minimumFractionDigits: minFraction,
        maximumFractionDigits: maxFraction,
        roundingMode: 'halfEven',
      }),
  ).format(value);
}

/** `Int.formatted()`: "1,234". */
export function integer(value: number, options?: FormatOptions): string {
  return number(Math.trunc(value), 0, 0, options);
}

/**
 * Byte counts, e.g. "184 MB" (`ByteCountFormatter`, countStyle .file: decimal units, adaptive precision — whole kB,
 * one decimal in MB, two from GB). CLDR spells kilobytes "kB".
 */
export function bytes(count: number, options?: FormatOptions): string {
  if (!Number.isFinite(count)) return DASH;
  const value = Math.max(0, count);
  if (value < 1000) return measure(value, 'byte', 0, 0, options);
  const units: [string, number, number][] = [
    ['kilobyte', 1e3, 0],
    ['megabyte', 1e6, 1],
    ['gigabyte', 1e9, 2],
    ['terabyte', 1e12, 2],
  ];
  for (let i = 0; i < units.length; i += 1) {
    const [unit, size, digits] = units[i];
    const next = units[i + 1];
    if (!next || value < next[1]) return measure(value / size, unit, 0, digits, options);
  }
  return DASH;
}
